import {isFeatureEnabled} from '@github-ui/feature-flags'
import {fuzzyFilter} from '@github-ui/fuzzy-score/fuzzy-filter'
import type {BaseItemPickerProps} from '@github-ui/item-picker-base/base-item-picker-props'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {URLS} from '@github-ui/item-picker-base/constants/urls'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {PlusIcon} from '@primer/octicons-react'
import {SelectPanel, type SelectPanelItemProps as ItemProps} from '@primer/react'
import type React from 'react'
import {useCallback, useDeferredValue, useEffect, useMemo, useRef, useState} from 'react'

import styles from './LabelPickerBase.module.css'

export type LabelPickerLabel = {
  id: string
  color: string
  name: string
  nameHTML: string
  description: string | null | undefined
  url: string
}

export type LabelPickerBaseProps = Omit<BaseItemPickerProps<LabelPickerLabel>, 'filterItems'> & {
  repo: string
  owner: string
  labels: LabelPickerLabel[]
  canCreateLabel: boolean
  suggestions?: LabelPickerLabel[]
  copilotSuggestedLabels?: LabelPickerLabel[]
  favoriteLabelIds?: string[]
  noLabelOption?: ExtendedItemProps<LabelPickerLabel>
  initialFilter?: string
  showEditLabelsButton?: boolean
  showNoMatchItem?: boolean
}

export type LabelPickerInternalProps = {
  totalNumberOfLabels: number
  searchLabels?: (query: string) => Promise<LabelPickerLabel[]>
  canCreateLabel: boolean
  repositoryId?: string
  setIsLoading?: (isLoading: boolean) => void
  onCreate?: CreateLabelMutationCallback
}

export type MutationCallback<TVariables, TResponse> = (
  variables: TVariables,
  options: {onSuccess: (response: TResponse | null) => void; onError: () => void},
) => void
export type CreateLabelVariables = {repositoryId: string; color: string; name: string}
export type CreateLabelMutationCallback = MutationCallback<CreateLabelVariables, LabelPickerLabel>

const DEFAULT_SUGGESTIONS: LabelPickerLabel[] = []
const FILTERED_RESULTS_LIMIT = 30

const labelsGroup: ItemGroup = {groupId: 'labels', header: {title: 'Selected labels', variant: 'filled'}}
const favoriteGroup: ItemGroup = {groupId: 'favorite', header: {title: 'Favorite', variant: 'filled'}}
const suggestionsGroup: ItemGroup = {groupId: 'suggestions', header: {title: 'Suggestions', variant: 'filled'}}
const copilotGroup: ItemGroup = {groupId: 'copilot', header: {title: 'Copilot suggestions', variant: 'filled'}}

export function LabelPickerBase({
  labels: selectedLabels,
  suggestions = DEFAULT_SUGGESTIONS,
  totalNumberOfLabels,
  copilotSuggestedLabels,
  favoriteLabelIds,
  searchLabels,
  onSelectionChange,
  insidePortal,
  anchorElement,
  nested = false,
  triggerOpen = true,
  initialFilter,
  owner,
  repo,
  showEditLabelsButton = true,
  noLabelOption = undefined,
  title,
  showNoMatchItem = false,
  canCreateLabel,
  repositoryId,
  isLoading = false,
  setIsLoading,
  onCreate,
}: LabelPickerBaseProps & LabelPickerInternalProps) {
  const removeLabelsLoadingEnabled = isFeatureEnabled('issues_react_remove_labels_loading')
  const responsiveOnNarrowEnabled = isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')
  const [filter, setFilter] = useState('')
  // Defer filter updates to keep input responsive during expensive list reconciliation
  const deferredFilterValue = useDeferredValue(filter)
  const filterTermCache = useRef<Set<string>>(new Set())
  const [isServerFetching, setIsServerFetching] = useState(false)
  const [loadedLabels, setLoadedLabels] = useState<Array<LabelPickerLabel & {__isNew__?: boolean}>>(suggestions)
  const {addToast} = useToastContext()
  const repoNwo = `${owner}/${repo}`

  // Synchronize loadedLabels with suggestions when they change
  useEffect(() => {
    setLoadedLabels(suggestions)
  }, [suggestions])

  const shouldServerFetch = searchLabels && totalNumberOfLabels > suggestions.length

  const sortedSelectedLabels = useMemo(
    () => selectedLabels.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [selectedLabels],
  )

  // Memoize key functions to avoid recreating them on every render
  const getLabelName = useCallback((l: LabelPickerLabel) => l.name, [])
  const getLabelNameAndDescription = useCallback((l: LabelPickerLabel) => `${l.name} ${l.description ?? ''}`, [])

  const items = useMemo(() => {
    if (!deferredFilterValue) {
      const relevantLabels = removeLabelsLoadingEnabled ? loadedLabels : suggestions

      // First, collect all the labels that are already selected
      const allLabels = [...sortedSelectedLabels]

      // Then add unselected copilot labels that aren't in selected labels
      const unselectedCopilotLabels = (copilotSuggestedLabels ?? []).filter(
        l => !selectedLabels.find(l2 => l2.id === l.id),
      )

      // Then add unselected regular labels that aren't in selected labels and unselected copilot labels
      const unselectedRegularLabels = relevantLabels.filter(
        l => !selectedLabels.find(l2 => l2.id === l.id) && !unselectedCopilotLabels.find(l2 => l2.id === l.id),
      )

      // Combine all labels
      allLabels.push(...unselectedCopilotLabels, ...unselectedRegularLabels)

      // If we render the picker with all the labels we loaded it will cause some serious jank.
      // Instead, only render a sensible page size of labels.
      return allLabels.slice(0, VALUES.labelsInitialLoadCount)
    }

    // For filtered results, include both loaded labels and copilot labels without duplicates
    const uniqueItemsMap = new Map()

    // Add loaded labels first
    for (const label of loadedLabels) {
      uniqueItemsMap.set(label.id, label)
    }
    // Add copilot labels, but only if they're not already in the map
    for (const label of copilotSuggestedLabels ?? []) {
      if (!uniqueItemsMap.has(label.id)) {
        uniqueItemsMap.set(label.id, label)
      }
    }

    const itemsToFilter = Array.from(uniqueItemsMap.values())

    return fuzzyFilter<LabelPickerLabel>({
      items: itemsToFilter,
      filter: deferredFilterValue,
      key: getLabelName,
      secondaryKey: getLabelNameAndDescription,
    }).slice(0, FILTERED_RESULTS_LIMIT)
  }, [
    deferredFilterValue,
    loadedLabels,
    removeLabelsLoadingEnabled,
    suggestions,
    sortedSelectedLabels,
    copilotSuggestedLabels,
    selectedLabels,
    getLabelName,
    getLabelNameAndDescription,
  ])

  const fetchSearchData = useCallback(
    async (query: string) => {
      if (!removeLabelsLoadingEnabled && query === '') {
        setLoadedLabels(suggestions)
        setIsServerFetching(false)
        return
      }

      if (removeLabelsLoadingEnabled && filterTermCache.current.has(query)) {
        setIsServerFetching(false)
        return
      }

      if (!searchLabels) return
      setIsServerFetching(true)

      try {
        setLoadedLabels(
          removeLabelsLoadingEnabled
            ? uniqById([...loadedLabels, ...(await searchLabels(query))])
            : await searchLabels(query),
        )
      } catch {
        // Temporarily disabling this until we have a standard way of displaying fetch errors
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotSearchLabels,
        })
      } finally {
        setIsServerFetching(false)
        if (removeLabelsLoadingEnabled) {
          filterTermCache.current.add(query)
        }
      }
    },
    [removeLabelsLoadingEnabled, searchLabels, suggestions, loadedLabels, addToast],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
  )

  const filterItems = useCallback(
    (value: string) => {
      const trimmedFilter = value.trim()
      if (filter !== trimmedFilter && shouldServerFetch) {
        setIsServerFetching(true)
        debounceFetchSearchData(trimmedFilter)
        setFilter(value)
        return
      }
      // Making sure we set the filter in case the user types in
      // a value before we complete the initial load of suggestions
      if (suggestions.length !== 0) setFilter(value)
    },
    [debounceFetchSearchData, filter, shouldServerFetch, suggestions.length],
  )

  const getItemKey = useCallback((label: LabelPickerLabel) => label.id, [])

  const createNewLabel = useCallback(
    (
      _: ItemProps,
      label: LabelPickerLabel,
      event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    ) => {
      event.preventDefault()
      event.stopPropagation()

      if (!repositoryId || !onCreate || !setIsLoading) return
      setIsLoading(true)

      onCreate(
        {
          repositoryId,
          color: label.color,
          name: label.name,
        },
        {
          onError: () => {
            // Temporarily disabling this until we have a standard way of displaying fetch errors
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: 'error',
              message: ERRORS.couldNotCreateLabel,
            })
            setIsLoading(false)
          },
          onSuccess: response => {
            if (response) {
              setLoadedLabels([{...response, __isNew__: true}, ...loadedLabels])
            }
            setIsLoading(false)
          },
        },
      )
    },
    [addToast, loadedLabels, onCreate, repositoryId, setIsLoading],
  )

  const convertToItemProps = useCallback(
    (label: LabelPickerLabel): ExtendedItemProps<LabelPickerLabel> => {
      if (noLabelOption && label.id === SPECIAL_VALUES.noLabelsData.id) {
        const noLabelItem = {...noLabelOption}
        noLabelItem.groupId = noLabelItem.selected ? labelsGroup.groupId : suggestionsGroup.groupId
        return noLabelItem
      }

      if (label.id === filter) {
        return {
          id: label.id,
          description: label.description || undefined,
          descriptionVariant: 'block',
          source: label,
          text: `Create new label: "${label.name}"`,
          trailingVisual: PlusIcon,
          className: styles.labelPickerItem,
          onAction: (item: ItemProps, event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) =>
            createNewLabel(item, label, event),
        }
      }

      // Check if this is a Copilot label and assign it to the that group
      const isCopilotLabel = (copilotSuggestedLabels ?? []).some(copilotLabel => copilotLabel.id === label.id)
      const isfavoriteLabel = (favoriteLabelIds ?? []).some(favoriteLabelId => favoriteLabelId === label.id)
      let groupId: string
      if (selectedLabels.find(l => l.id === label.id)) {
        groupId = labelsGroup.groupId
      } else if (isfavoriteLabel) {
        groupId = favoriteGroup.groupId
      } else if (isCopilotLabel) {
        groupId = copilotGroup.groupId
      } else {
        groupId = suggestionsGroup.groupId
      }

      return {
        id: label.id,
        description: label.description ?? undefined,
        descriptionVariant: 'block',
        children: (
          <VerifiedHTMLText
            // eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string
            html={label.nameHTML as SafeHTMLString}
            className={styles.labelNameText}
          />
        ),
        source: label,
        groupId,
        leadingVisual: () => (
          <div
            style={{
              background: `#${label.color}`,
              borderColor: `#${label.color}`,
            }}
            className={styles.labelColorBox}
          />
        ),
      }
    },
    [noLabelOption, filter, copilotSuggestedLabels, favoriteLabelIds, selectedLabels, createNewLabel],
  )

  const groups: ItemGroup[] = useMemo(() => {
    const itemGroups = []
    if (selectedLabels.length > 0) {
      itemGroups.push(labelsGroup)
    }
    const hasFavoriteWhichAreNotSelected = (favoriteLabelIds ?? []).some(
      favoriteLabelId => !selectedLabels.some(selectedLabel => selectedLabel.id === favoriteLabelId),
    )
    if (hasFavoriteWhichAreNotSelected) {
      itemGroups.push(favoriteGroup)
    }

    // Add Copilot suggested labels group
    const hasCopilotLabels = (copilotSuggestedLabels ?? []).some(
      label =>
        !selectedLabels.some(selectedLabel => selectedLabel.id === label.id) &&
        favoriteLabelIds?.indexOf(label.id) === -1,
    )
    if (hasCopilotLabels) {
      itemGroups.push(copilotGroup)
    }

    if (items.length > 0 && items.length - selectedLabels.length > 0) {
      itemGroups.push(suggestionsGroup)
    }
    return itemGroups
  }, [selectedLabels, favoriteLabelIds, copilotSuggestedLabels, items.length])

  const editLabelsPath = useMemo(() => {
    if (!repoNwo || !ssrSafeDocument?.location) return undefined
    const baseUrl = ssrSafeDocument.location.origin
    return `${baseUrl}/${repoNwo}${URLS.labelPageSuffix}`
  }, [repoNwo])

  const onEditLabels = useCallback(
    (e: React.MouseEvent) => {
      if (ssrSafeDocument?.location?.href && showEditLabelsButton && editLabelsPath) {
        // if the mouse wheel is clicked, do nothing
        if (e.button === 1) return

        // if a keyboard modifier is used, do nothing as the underlying default logic will
        // already open this into a new tab.
        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return

        e.preventDefault()
        ssrSafeDocument.location.href = editLabelsPath
      }
    },
    [editLabelsPath, showEditLabelsButton],
  )

  const anchorRef = useRef<HTMLButtonElement>(null)

  const showNoMatchMessage = useMemo(() => {
    const noPickerItems = !isServerFetching && !filter && items.length === 0
    const noFilterResults = !(showNoMatchItem && canCreateLabel && !isServerFetching) && !!filter && items.length === 0
    return noPickerItems || noFilterResults
  }, [isServerFetching, filter, items.length, showNoMatchItem, canCreateLabel])

  return (
    <div className={styles.labelPickerContainer}>
      <ItemPicker
        loading={removeLabelsLoadingEnabled ? isLoading : isLoading || isServerFetching}
        items={items}
        initialSelectedItems={selectedLabels}
        title={title || LABELS.labelsHeader}
        filterItems={filterItems}
        getItemKey={getItemKey}
        convertToItemProps={convertToItemProps}
        placeholderText={'Filter labels'}
        selectionVariant={items.length === 0 ? 'single' : 'multiple'}
        onSelectionChange={onSelectionChange}
        renderAnchor={props => anchorElement(props, anchorRef)}
        groups={!filter ? groups : undefined}
        insidePortal={insidePortal}
        height={'large'}
        width={'medium'}
        nested={nested}
        resultListAriaLabel={'Label results'}
        triggerOpen={triggerOpen}
        initialFilter={initialFilter}
        selectPanelRef={anchorRef}
        keybindingCommandId="item-pickers:open-labels"
        eventsTag="label"
        customNoMatchItem={
          canCreateLabel && showNoMatchItem && filter && !isServerFetching
            ? ({
                id: filter,
                color: 'aaaaaa',
                name: filter,
                nameHTML: filter,
                description: undefined,
                url: '',
              } as LabelPickerLabel)
            : undefined
        }
        suppressNoMatchItem
        improvedNoMatchAccessibility={showNoMatchMessage}
        noMatchMessage={
          showNoMatchMessage
            ? {
                title: 'No labels were found',
                body: 'Try searching with a different query for results.',
                variant: 'empty',
              }
            : undefined
        }
        secondaryAction={
          showEditLabelsButton ? (
            <SelectPanel.SecondaryActionLink onClick={onEditLabels} href={editLabelsPath} size={'small'}>
              {LABELS.editLabels}
            </SelectPanel.SecondaryActionLink>
          ) : undefined
        }
        responsiveOnNarrow={responsiveOnNarrowEnabled}
        focusPrependedElements
      />
    </div>
  )
}

const uniqById = (arr: LabelPickerLabel[]) => {
  return arr.filter((i, idx, a) => a.findIndex(item => item.id === i.id) === idx)
}

try{ LabelPickerBase.displayName ||= 'LabelPickerBase' } catch {}